import { useState, useRef, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import Cookies from "js-cookie";
import apiClient from "../../services/apiService";
import { getErrorMessage } from "../../utils/getErrorMessage";

const Verify2FA = () => {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timer, setTimer] = useState(58);
  const navigate = useNavigate();
  const otpRefs = useRef([]);

  const email = sessionStorage.getItem("2faEmail");

  useEffect(() => {
    if (!email) {
      navigate("/login");
      return;
    }
  }, [email, navigate]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // --- API MUTATIONS ---
  const verify2FAMutation = useMutation({
    mutationFn: (payload) =>
      apiClient.post("/admin/login-verify", payload),
    onSuccess: (response) => {
      if (response.data.message?.token) {
        toast.success("2FA verification successful!");
        Cookies.set("authToken", response.data.message.token, {
          secure: true,
          sameSite: "Strict",
        });
        Cookies.set("userName", response.data.message.user.name, {
          secure: true,
          sameSite: "Strict",
        });
        Cookies.set("userId", response.data.message.user.id, {
          secure: true,
          sameSite: "Strict",
        });
        
        sessionStorage.removeItem("2faEmail");
        
        setTimeout(() => navigate("/"), 1000);
      }
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Invalid OTP."));
    },
  });

  const resendOTPMutation = useMutation({
    mutationFn: () =>
      apiClient.post("/admin/resend-2fa", { email }),
    onSuccess: () => {
      toast.success("OTP resent to your email");
      setTimer(58);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to resend OTP."));
    },
  });

  const handleOtpChange = (value, index) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    if (value && index < 3) otpRefs.current[index + 1].focus();
  };

  const handlePaste = (e) => {
    const data = e.clipboardData.getData("text").trim();
    if (!/^\d{4}$/.test(data)) return; // Ensure it's exactly 4 digits

    const digits = data.split("");
    setOtp(digits);
    otpRefs.current[3].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleVerify2FA = () => {
    verify2FAMutation.mutate({ email, code: parseInt(otp.join("")) });
  };

  const Logo = () => (
    <div className="mb-6 flex flex-col items-center">
      <img
        src="/images/logos/logo.png"
        alt="Trulixx Fashion Logo"
        className="h-16 w-auto object-contain"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-[32px] shadow-sm p-10 py-12 text-center">
        <Logo />

        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-gray-900">
              Verify Your Identity
            </h2>
            <p className="text-[#9397A1] text-base px-4">
              Enter the 4-digit code sent to your email
            </p>
          </div>

          <div className="flex justify-between gap-2 py-4">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpRefs.current[index] = el)}
                type="text"
                maxLength={1}
                value={digit}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={verify2FAMutation.isPending}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="w-12 h-12 text-center text-lg font-medium bg-gray-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-[#DBCBB9] outline-none transition-all"
              />
            ))}
          </div>

          <button
            onClick={handleVerify2FA}
            disabled={otp.join("").length !== 4 || verify2FAMutation.isPending}
            className={`w-full text-gray-800 font-medium py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 ${
              otp.join("").length === 4 && !verify2FAMutation.isPending
                ? "bg-[#E2D1C3] hover:bg-[#d4c1b2]"
                : "bg-gray-200 opacity-50"
            }`}
          >
            {verify2FAMutation.isPending ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              "Verify Code"
            )}
          </button>

          <div className="text-sm text-gray-400">
            {timer > 0 ? (
              <p>
                Resend code in{" "}
                <span className="text-[#556B2F] font-medium">
                  {timer} secs
                </span>
              </p>
            ) : (
              <p>
                Didn't receive the code?{" "}
                <button
                  onClick={() => resendOTPMutation.mutate()}
                  disabled={resendOTPMutation.isPending}
                  className="text-[#556B2F] font-medium hover:underline disabled:opacity-50"
                >
                  {resendOTPMutation.isPending ? "Sending..." : "Resend code"}
                </button>
              </p>
            )}
          </div>

          <button
            onClick={() => {
              sessionStorage.removeItem("2faEmail");
              navigate("/login");
            }}
            className="text-green-700 text-sm font-medium hover:underline block w-full"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default Verify2FA;
